import { type RefObject, useEffect, useRef, useState } from 'react';

/** How far the finger travels for each pixel the indicator moves: a pull has weight. */
const RESISTANCE = 0.5;
/** Far enough that a scroll back to the top is not read as a request. */
export const PULL_THRESHOLD = 64;
const PULL_MAX = 110;
/** How long the indicator lingers once the reads are back, so a fast one is still seen to have happened. */
export const SETTLE_MS = 300;
/** The longest the indicator waits for a refresh that never reports itself. */
export const GIVE_UP_MS = 1500;

export type PullPhase = 'idle' | 'pulling' | 'refreshing';

export interface PullState {
  phase: PullPhase;
  /** How far the indicator is drawn down, in px. */
  distance: number;
  /** Past the threshold: letting go now refreshes. */
  armed: boolean;
}

const IDLE: PullState = { phase: 'idle', distance: 0, armed: false };

/**
 * Whether anything between `node` and the document is scrolled away from its
 * top. The document is not the only thing that scrolls — a pane can — and a
 * pull read off the wrong one would take over the gesture that scrolls a
 * list back up.
 */
export const scrolledAway = (node: HTMLElement): boolean => {
  for (let element: HTMLElement | null = node; element; element = element.parentElement) {
    if (element.scrollTop > 0) return true;
  }
  const root = typeof document !== 'undefined' ? document.scrollingElement : null;
  return !!root && root.scrollTop > 0;
};

/**
 * Pull down from the top of the page to re-read it — the gesture every phone
 * user already tries first.
 *
 * The dispatch dashboard's phone shell refreshes its pages this way and draws
 * no refresh button (a second, less discoverable way to do it), so a page
 * framed inside it has to offer the same gesture itself: the shell's own
 * pull-to-refresh cannot reach into a frame.
 *
 * Only a pull that STARTS at the top counts — mid-page it is an ordinary
 * scroll. Once armed, the indicator stays until the reads report back
 * (`refreshing` goes true and then false), or gives up after a moment rather
 * than spinning for ever over a refresh that never said it began.
 */
export function usePullToRefresh(
  target: RefObject<HTMLElement | null>,
  { onRefresh, refreshing, enabled = true }: { onRefresh: () => void; refreshing: boolean; enabled?: boolean },
): PullState {
  const [state, setState] = useState<PullState>(IDLE);
  const start = useRef<number | null>(null);
  const distance = useRef(0);
  const refreshRef = useRef(onRefresh);
  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  // A refresh reported as started, so that its end can be told from its not
  // having begun yet.
  const sawRefreshing = useRef(false);

  useEffect(() => {
    const node = target.current;
    if (!node || !enabled) return;

    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1 || scrolledAway(node)) {
        start.current = null;
        return;
      }
      start.current = event.touches[0].clientY;
    };

    const onMove = (event: TouchEvent) => {
      if (start.current === null) return;
      const pulled = event.touches[0].clientY - start.current;
      if (pulled <= 0) {
        distance.current = 0;
        setState((current) => (current.phase === 'pulling' ? IDLE : current));
        return;
      }
      // Ours now, not the page's: without this the browser bounces the page
      // under the indicator (or, standalone, reloads the whole tab).
      if (event.cancelable) event.preventDefault();
      const next = Math.min(PULL_MAX, pulled * RESISTANCE);
      distance.current = next;
      setState({ phase: 'pulling', distance: next, armed: next >= PULL_THRESHOLD });
    };

    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      if (distance.current >= PULL_THRESHOLD) {
        sawRefreshing.current = false;
        setState({ phase: 'refreshing', distance: PULL_THRESHOLD, armed: true });
        refreshRef.current();
      } else {
        setState(IDLE);
      }
      distance.current = 0;
    };

    node.addEventListener('touchstart', onStart, { passive: true });
    node.addEventListener('touchmove', onMove, { passive: false });
    node.addEventListener('touchend', onEnd);
    node.addEventListener('touchcancel', onEnd);
    return () => {
      node.removeEventListener('touchstart', onStart);
      node.removeEventListener('touchmove', onMove);
      node.removeEventListener('touchend', onEnd);
      node.removeEventListener('touchcancel', onEnd);
    };
  }, [target, enabled]);

  // Settle a moment after the reads come back — or, if they never said they
  // started, after a longer one, so the spinner cannot outlive the question
  // it answers.
  useEffect(() => {
    if (state.phase !== 'refreshing') return;
    if (refreshing) {
      sawRefreshing.current = true;
      return;
    }
    const timer = window.setTimeout(
      () => {
        sawRefreshing.current = false;
        setState(IDLE);
      },
      sawRefreshing.current ? SETTLE_MS : GIVE_UP_MS,
    );
    return () => window.clearTimeout(timer);
  }, [state.phase, refreshing]);

  return state;
}
